// React and Router imports
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

// Firebase imports
import { auth } from "../../firebase.js"

// Components
import Sidebar from "../shared/Sidebar";
import Comments from "./Comments.js";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import TaskDetailModal from "../shared/TaskDetailModal";
import SaveAsTemplateDialog from "../shared/templates/SaveAsTemplateDialog";
import useTaskDetailModal from "../../utils/useTaskDetailModal";

// Utils
import { downloadAsMarkdown, downloadAsJSON, downloadBulkAsZip, exportPersonalPdf, exportPublicPdf, loadPdfOptions, savePdfOptions } from "../../utils/exportUtils";
import PdfExportOptions from "../shared/PdfExportOptions";
import { wrapTablesInScrollContainers } from "../../utils/contentTransforms";
import SpaceTableOfContents from "../shared/SpaceTableOfContents";

// Styles
import "../../css/DocumentationMainPage.css"
import "../../css/Document.css"
import "../../css/DocumentationHomeComponent.css"
import "../../css/Create.css"
import "../../css/ConflictResolver.css"
import "../../css/HomePage.css"
import "../../css/TaskReference.css"
import "../../css/DocViewerMakeover.css"

function Document() {

        const { enhancedRightClickEnabled } = useQuickLinks();
        const { type, name, uid } = useParams(); 
        const [userUid, setUserUid] = useState("")
        const [userEmail, setUserEmail] = useState("")
        const [loading, setLoading] = useState(true)
        const [loadingMessage, setLoadingMessage] = useState('Checking authentication...')
        const [hasPermission, setHasPermission] = useState(true)
        const [documentResponseData, setDocumentResponseData] = useState("")
        // Raw editor HTML (before display transforms) for "Save as template" (BBD-109)
        const [rawContent, setRawContent] = useState("")
        const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false)
        const [canEdit, setCanEdit] = useState(true)
        const [parentDocument, setParentDocument] = useState("")
        const [published, setPublished] = useState(true)
        const [shareOnPublicRegistry, setShareOnPublicRegistry] = useState(false)
        const [isShareVisible, setIsShareVisible] = useState(false);
        const [authResolved, setAuthResolved] = useState(false);
        const [isFavorited, setIsFavorited] = useState(false);
        const [favoriteId, setFavoriteId] = useState(null);
        const [favoriteLoading, setFavoriteLoading] = useState(false);
        const navigate = useNavigate()
        const { activeTask, open: openTaskModal, close: closeTaskModal } = useTaskDetailModal();

        const [customLink, setCustomLink] = useState("")
        const [customLinkTitle, setCustomLinkTitle] = useState("")
        const [discordLink, setDiscordLink] = useState("")
        const [linkedInLink, setLinkedInLink] = useState("")
        const [youtubeLink, setYoutubeLink] = useState("")
        const [githubLink, setGithubLink] = useState("")
        const [twitterLink, setTwitterLink] = useState("")
        const [blueskyLink, setBlueskyLink] = useState("")

        const [exportModal, setExportModal] = useState(null); // { format: 'markdown'|'json'|'pdf' }
        const [exportScope, setExportScope] = useState('current');
        const [exportLoading, setExportLoading] = useState(false);
        // PDF export (BBD-119): options are remembered between exports; status
        // carries the progress / error / "we'll notify you" line in the modal.
        const [pdfOptions, setPdfOptions] = useState(loadPdfOptions);
        const [pdfStatus, setPdfStatus] = useState(null);

        const isSharedDocument = userUid && uid !== userUid;
        const hasChildren = Boolean(documentResponseData.children && String(documentResponseData.children).trim());

        useEffect(() => {
                setLoadingMessage('Checking authentication...');
                const unsubscribe = auth.onAuthStateChanged((user) => {
                    if (user) {
                                setUserUid(user.uid);
                                setUserEmail(user.email); 
                        } else {
                                setUserUid(null);
                                setUserEmail(null); 
                                // setHasPermission(false)
                                setLoading(false)
                        }
                        setAuthResolved(true);
                });
            
                return () => { unsubscribe(); };
        }, []);

        useEffect(() => {
                // Removed checkIfFavorited call since it's now handled in fetchDocumentContent
        }, [userUid, documentResponseData]);

        // Theme cleanup on unmount



        useEffect(() => {
                if (uid && authResolved) { 
                        setLoading(true); // Always set loading to true when fetching new document
                        setLoadingMessage('Fetching document content...');
                        fetchDocumentContent();
                }
        }, [type, name, uid, userUid, userEmail]);

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      url: "/login",
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      url: "/register",
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      url: "/my-notebook",
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      url: "https://www.youtube.com/@schoolofintology4062",
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      url: "https://discord.gg/DYkESHwgVA",
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];

const loggedInOptions = [
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => navigate("/"),
      },
  {
    label: "Your Documents",
    url: "/documentation",
    onClick: () => navigate("/documentation"),
  },
  {
    label: "Your Walkthroughs",
    url: "/walkthroughs",
    onClick: () => navigate("/walkthroughs"),
  },
  {
    label: "Your Notebook",
    url: "/my-notebook",
    onClick: () => navigate("/my-notebook"),
  },
  {
    label: "Your Workspace",
    url: "/workspaces",
    onClick: () => navigate("/workspaces"),
  },
  {
    label: "Account Settings",
    url: `/account/${uid}`,
    onClick: () => navigate(`/account/${uid}`),
  },
];

  // Use loggedInOptions if signed in, otherwise loggedOutOptions
  const menuOptions = uid ? loggedInOptions : loggedOutOptions;
  const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

        // Function to check if the current document is already favorited
        const checkIfFavorited = async (documentData) => {
                try {
                        const user = auth.currentUser;
                        if (!user) return;
                        
                        const token = await user.getIdToken();
                        
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/favorites/get-favorites/${user.uid}/`, {
                        method: "GET",
                        headers: {
                                "Content-Type": "application/json",
                                "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                        }
                        });
                        
                        if (response.ok) {
                        const favorites = await response.json();
                        
                        // Check if this document is in the favorites list
                        const favorite = favorites.find(fav => 
                                fav.document_type === type && 
                                fav.document_name === name && 
                                fav.document_id === String(documentData.id)
                        );
                        
                        if (favorite) {
                                setIsFavorited(true);
                                setFavoriteId(favorite.id);
                        } else {
                                setIsFavorited(false);
                                setFavoriteId(null);
                        }
                        }
                } catch (error) {
                        console.error("Error checking if document is favorited:", error);
                }
        };

        const fetchDocumentContent = async () => {
                setLoading(true); // Ensure loading is always set to true when starting fetch
                setLoadingMessage('Fetching document data...');
                setHasPermission(true)
                setPublished(true)
                // if (!userUid || !userEmail) {
                //         setLoading(false)
                //         return
                // }

                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) {
                                console.error("User is not authenticated.");
                        }
                        setLoadingMessage('Loading document content...');
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/documentation/${type}/${name}/${uid}/`, {
                                method: 'GET',
                                headers: {
                                        ...(userUid && { 'requesting-user-uid': userUid }),
                                        ...(userEmail && { 'requesting-user-email': userEmail }),
                                        ...(token && { 'auth-token': `Bearer ${token}` }),
                ...(token && { 'Authorization': `${token}` }),
                                        'requesting-type': 'view'
                                }
                        })

                        const data = await response.json()

                        if (data.is_public) {
                                setHasPermission(true)
                        } else if (response.status === 403) {
                                setHasPermission(false)
                                setLoading(false)
                                return
                        }

                        if (response.status === 404) {
                                setPublished(false)
                                setLoading(false)
                                return
                        }

                        let canEditValue = false
                        if (!userUid) {
                                canEditValue = false;
                        } else if (userUid === uid) {
                                canEditValue = true;
                        } else if (data.permissions) {
                                canEditValue = data.permissions.can_edit
                                setCanEdit(data.permissions.can_edit);
                        }
                        
                        setCanEdit(canEditValue);

                        if (!data.completed && !canEditValue) {
                                setPublished(false)
                                setLoading(false)
                                return
                        }

                        setLoadingMessage('Processing document content...');
                        const updatedContent = transformContent(data.content);
                        setRawContent(data.content || "");
                        setDocumentResponseData({ ...data, content: updatedContent });
                        setParentDocument(data.parent_page)
                        setYoutubeLink(data.youtube_channel)
                        setCustomLink(data.custom_domain)
                        setCustomLinkTitle(data.custom_domain_title)
                        setDiscordLink(data.discord_server)
                        setLinkedInLink(data.linkedin_link)
                        setBlueskyLink(data.bluesky_link)
                        setGithubLink(data.github_link)
                        setTwitterLink(data.twitter_link)
                        
                        // Check if document is favorited if user is logged in
                        if (userUid) {
                                setLoadingMessage('Checking favorite status...');
                                await checkIfFavorited(data); // Pass the data directly instead of relying on state
                        }
                        
                        setLoading(false)
                } catch (error) {
                        setLoading(false)
                        console.error("Error fetching document data: ", error)
                }
        }
        
        const handleDeleteClick = async () => {
                const isConfirmed = window.confirm("Are you sure you want to delete this document?\n\n-This action cannot be undone.\n-Any child pages will be deleted as well.");
                if (!isConfirmed) {
                        return;
                }
                
                setLoading(true);
                setLoadingMessage('Deleting document...');
                
                const data = {
                        parentDocument: parentDocument
                }

                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                
                        if (!token) {
                                console.error("User is not authenticated.");
                                setLoading(false);
                                return;
                        }

                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/documentation/delete/${type}/${name}/${uid}/`, {
                                method: "DELETE",
                                body: JSON.stringify(data),
                                headers: { 
                                        "Content-Type": "application/json",
                                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                },
                        })

                        if (response.ok) {
                                setLoadingMessage('Document deleted successfully! Redirecting...');
                                navigate("/documentation")
                        } else {
                                console.log("Failed to delete document");
                                setLoading(false);
                        }
                } catch (error) {
                        console.error("Error deleting document: ", error);
                        setLoading(false);
                }
        }

        const handleArchiveDocument = async () => {
                const isCurrentlyArchived = documentResponseData.is_archived;
                const action = isCurrentlyArchived ? 'unarchive' : 'archive';
                const confirmMsg = isCurrentlyArchived
                        ? `Unarchive this ${type}?`
                        : `Archive this ${type}? All child documents will also be archived.`;
                if (!window.confirm(confirmMsg)) return;
                try {
                        const user = auth.currentUser;
                        if (!user) { alert("You must be logged in."); return; }
                        const token = await user.getIdToken();
                        const endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/${action}/${type}/${name}/${uid}/`;
                        const response = await fetch(endpoint, {
                                method: 'PUT',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `${token}`,
                                        'Auth-Token': `Bearer ${token}`,
                                        'Requesting-User-Uid': user.uid
                                }
                        });
                        if (!response.ok) {
                                const data = await response.json();
                                throw new Error(data.error || `Failed to ${action} document`);
                        }
                        window.location.reload();
                } catch (error) {
                        console.error(`Error ${action}ing document:`, error);
                        alert(`Error: ${error.message}`);
                }
        }

        const formatTimestamp = (timestamp) => {
                const date = new Date(timestamp);
                return date.toLocaleString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                });
        };

        const formatFileSize = (bytes) => {
                if (bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        };

        const handleAttachmentDownload = async (attachment) => {
                try {
                        // Use the new download endpoint which handles tracking automatically
                        const downloadUrl = `https://bluebirddocumentationadmin.pythonanywhere.com/attachments/${attachment.id}/download/`;
                        
                        // Create download link and trigger download
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = attachment.file_name;
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                } catch (error) {
                        console.error('Error downloading attachment:', error);
                        alert('Error downloading file. Please try again.');
                }
        };

        const transformContent = (htmlContent) => {
                if (!htmlContent) return "";
        
                const parser = new DOMParser();
                const doc = parser.parseFromString(htmlContent, "text/html");
        
                // Demote headings two levels for display (h1→h3 … h4→h6) so all four
                // editor levels stay distinct while h1/h2 remain reserved for page
                // chrome. Deepest first so freshly created tags aren't re-demoted.
                [["h4", "h6"], ["h3", "h5"], ["h2", "h4"], ["h1", "h3"]].forEach(([from, to]) => {
                        doc.querySelectorAll(from).forEach((heading) => {
                                const demoted = document.createElement(to);
                                demoted.innerHTML = heading.innerHTML;
                                heading.replaceWith(demoted);
                        });
                });
        
                // Wide tables scroll inside their own container instead of
                // extending past the document card's right edge (BBD-71).
                wrapTablesInScrollContainers(doc);

                return doc.body.innerHTML;
        };

        const handleContentClick = (e) => {
                const chip = e.target.closest('.task-reference');
                if (!chip) return;
                openTaskModal(
                        chip.getAttribute('data-task-id'),
                        chip.getAttribute('data-board-id'),
                        chip.getAttribute('data-task-db-id')
                );
        };

        const toggleFavorite = async () => {
                if (favoriteLoading) return; // Prevent multiple clicks
                setFavoriteLoading(true);
                
                try {
                        const user = auth.currentUser;
                        if (!user) {
                        alert("You must be logged in to favorite documents");
                        setFavoriteLoading(false);
                        return;
                        }
                        
                        const token = await user.getIdToken();
                        
                        if (isFavorited) {
                        // Remove from favorites
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/favorites/remove/${favoriteId}/`, {
                                method: "DELETE",
                                headers: {
                                "Content-Type": "application/json",
                                "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                }
                        });
                        
                        if (response.ok) {
                                setIsFavorited(false);
                                setFavoriteId(null);
                        }
                        } else {
                        // Add to favorites
                        const isSharedDocument = documentResponseData.author !== userUid;
                        
                        const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/users/favorites/add/", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                    "document-author-uid": uid  // Adding the author UID from params
                                },
                                body: JSON.stringify({
                                document_type: type,
                                document_id: documentResponseData.id,
                                document_name: name,
                                is_shared: isSharedDocument  // This is extra info that might be useful for the frontend
                                })
                        });
                        
                        if (response.ok) {
                                const data = await response.json();
                                setIsFavorited(true);
                                if (data.favorite_id) {
                                setFavoriteId(data.favorite_id);
                                }
                        }
                        }
                } catch (error) {
                        console.error("Error toggling favorite status:", error);
                } finally {
                        setFavoriteLoading(false);
                }
        };

        function capitalizeFirstLetter(val) {
                return String(val).charAt(0).toUpperCase() + String(val).slice(1);
        }

        const handleExportMarkdown = () => {
                downloadAsMarkdown(
                        documentResponseData.content,
                        name,
                        {
                                createdAt: formatTimestamp(documentResponseData.created_at),
                                updatedAt: formatTimestamp(documentResponseData.updated_at),
                                views: documentResponseData.views
                        }
                );
        };

        const handleExportJSON = () => {
                downloadAsJSON({
                        id: documentResponseData.id,
                        type,
                        name,
                        content: documentResponseData.content,
                        views: documentResponseData.views,
                        created_at: documentResponseData.created_at,
                        updated_at: documentResponseData.updated_at,
                        is_public: documentResponseData.is_public,
                        completed: documentResponseData.completed,
                        attachments: documentResponseData.attachments || []
                }, name);
        };

        const fetchPersonalDescendants = async (childrenText, token, result = [], depth = 0) => {
                if (depth > 4 || !childrenText) return result;
                const names = String(childrenText).split(',').map(n => n.trim()).filter(Boolean);
                for (const childName of names) {
                        try {
                                const response = await fetch(
                                        `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/page/${encodeURIComponent(childName)}/${uid}/`,
                                        {
                                                headers: {
                                                        ...(userUid && { 'requesting-user-uid': userUid }),
                                                        ...(token && { 'auth-token': `Bearer ${token}` }),
                                                        ...(token && { 'Authorization': `${token}` }),
                                                        'requesting-type': 'view'
                                                }
                                        }
                                );
                                if (!response.ok) continue;
                                const data = await response.json();
                                result.push({ title: childName, data });
                                if (data.children) {
                                        await fetchPersonalDescendants(data.children, token, result, depth + 1);
                                }
                        } catch (err) {
                                console.error(`Failed to fetch child document: ${childName}`, err);
                        }
                }
                return result;
        };

        const handleExportClick = (format) => {
                // PDF always opens the modal: it holds the page options and the
                // progress of the server-side render.
                if (hasChildren || format === 'pdf') {
                        setExportScope('current');
                        setPdfStatus(null);
                        setExportModal({ format });
                } else {
                        if (format === 'markdown') handleExportMarkdown();
                        else handleExportJSON();
                }
        };

        const handleExportPdf = async () => {
                setExportLoading(true);
                setPdfStatus({ state: 'working', message: 'Generating your PDF…' });
                savePdfOptions(pdfOptions);
                try {
                        const user = auth.currentUser;
                        // Signed-out readers can only be looking at a public
                        // document, which has its own account-less route.
                        const result = user
                                ? await exportPersonalPdf({
                                        token: await user.getIdToken(),
                                        type,
                                        name,
                                        author: uid,
                                        includeChildren: hasChildren && exportScope === 'all',
                                        options: pdfOptions,
                                })
                                : await exportPublicPdf({ type, name, author: uid, options: pdfOptions });
                        if (result.status === 'queued') {
                                setPdfStatus({ state: 'queued', message: "This is a large export, so it's being prepared in the background. We'll notify you when it's ready." });
                        } else {
                                setExportModal(null);
                        }
                } catch (err) {
                        console.error('PDF export failed:', err);
                        setPdfStatus({ state: 'error', message: err.message });
                } finally {
                        setExportLoading(false);
                }
        };

        const handleExportConfirm = async () => {
                const format = exportModal.format;
                if (format === 'pdf') {
                        await handleExportPdf();
                        return;
                }
                if (exportScope === 'current') {
                        if (format === 'markdown') handleExportMarkdown();
                        else handleExportJSON();
                        setExportModal(null);
                        return;
                }
                setExportLoading(true);
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        const rootDoc = { title: name, data: documentResponseData };
                        const descendants = await fetchPersonalDescendants(documentResponseData.children, token);
                        await downloadBulkAsZip([rootDoc, ...descendants], format, name);
                } catch (err) {
                        console.error('Export failed:', err);
                        alert('Export failed. Please try again.');
                } finally {
                        setExportLoading(false);
                        setExportModal(null);
                }
        };

        const handleShareClick = () => {
                setIsShareVisible(!isShareVisible)
        }

        const toggleShareOnPublicRegistry = (e) => setShareOnPublicRegistry(e.target.checked);

        const link = `https://bluebird-documentation.com/documentation/${type}/${name}/${uid}`;

        const copyToClipboard = () => {
                navigator.clipboard.writeText(link);
                alert("Link copied to clipboard!");
        };

        const navigateToSocialMediaPage = (url) => () => {
                if (url) {
                        window.open(url, '_blank', 'noopener,noreferrer');
                } else {
                        console.warn("Invalid URL");
                }
        };

        const handleHomeButtonClick = () => { navigate("/") }
        const handleLoginButtonClick = () => { navigate("/login") }
        const handleRegisterButtonClick = () => { navigate("/register") }

        if (loading) {
                return (
                        <>
                            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                                <div className="coffee-cup-loader">
                                    <div className="cup">
                                        <div className="cup-body"></div>
                                        <div className="cup-handle"></div>
                                        <div className="cup-base"></div>
                                        <div className="cup-shadow"></div>
                                        <div className="steam steam1"></div>
                                        <div className="steam steam2"></div>
                                        <div className="steam steam3"></div>
                                    </div>
                                </div>
                                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
                            </div>
                        </>
                )
        }

        if (loading === false && hasPermission === false) {
                return (
                        <div 
                            className="DocumentationMainPage__div DocViewer"
                            onContextMenu={
                                enhancedRightClickEnabled
                                ? (e) => {
                                    e.preventDefault();
                                    showContextMenu(e.clientX, e.clientY);
                                    }
                                : undefined
                            }
                        >
                                {/* <Sidebar /> */}
                                <div className="DocumentationMainPage__main-content">
                                        <p className="large">It looks like you don't have permission to view this document.</p>
                                        <p className="medium">
                                                <strong>You might just need to sign in</strong>. If this is your document, make sure you're signed in. If 
                                                this issue persists, this could be a bug on our end. We are very sorry for the inconvenience; please let us know 
                                                this has happened.
                                        </p>
                                        <p className="medium">
                                                <strong>If this is not your document</strong>, check with the original author of the document to see if you have correct
                                                permissions.
                                        </p>
                                        <p className="medium">
                                                <strong>Was this document shared with you by email?</strong> Open the link from that email instead —
                                                it lets you view the document without needing an account.
                                        </p>
                                        <button onClick={handleHomeButtonClick} style={{ marginRight: "10px" }} className="ConflictResolver__button">Home</button>
                                        <button onClick={handleLoginButtonClick} style={{ marginRight: "10px" }} className="ConflictResolver__button">Login</button>
                                        <button onClick={handleRegisterButtonClick} className="ConflictResolver__button">Register</button>
                                </div>
                                {enhancedRightClickEnabled && ContextMenuComponent}
                        </div>
                )
        }

        if (loading === false && published === false) {
                return (
                        <div 
                            className="DocumentationMainPage__div DocViewer"
                            onContextMenu={
                                enhancedRightClickEnabled
                                ? (e) => {
                                    e.preventDefault();
                                    showContextMenu(e.clientX, e.clientY);
                                    }
                                : undefined
                            }
                        >
                                <Sidebar viewingSharedDocument={isSharedDocument} />
                                <div className="DocumentationMainPage__main-content">
                                        <p className="large">It looks you have permission to view this document, but is has not been published yet.</p>
                                        <p className="medium">
                                                <strong>As of the current version</strong>, only collaborators that have "Can Edit" permission can view/edit documents that 
                                                are not published yet. Otherwise, once the document is marked as published, you will be able to view the document.
                                        </p>
                                </div>
                                {enhancedRightClickEnabled && ContextMenuComponent}
                        </div>
                )
        }

        return (
                <div 
                    className="DocumentationMainPage__div DocViewer"
                    onContextMenu={
                        enhancedRightClickEnabled
                        ? (e) => {
                            e.preventDefault();
                            showContextMenu(e.clientX, e.clientY);
                            }
                        : undefined
                    }
                >
                        <Sidebar viewingSharedDocument={isSharedDocument} />

                        <div className="Document__floating-div">
                                {userUid && (
                                        <i 
                                            className={`fa-${isFavorited ? 'solid' : 'regular'} fa-star Document__icon-padding Document__icon-hover`} 
                                            title={isFavorited ? "Remove from favorites" : "Add to favorites"} 
                                            onClick={toggleFavorite} 
                                            style={{ cursor: "pointer" }}
                                        ></i>
                                )}
                                <div className="Document__icon-separator"></div>
                                {documentResponseData.is_public ? (
                                        <i className="fa-solid fa-share Document__icon-padding Document__icon-hover" title="Share" onClick={handleShareClick} style={{ marginLeft: "0px" }}></i>
                                ) : (
                                        <></>
                                )}
                                {canEdit ? (
                                        <Link to={`/documentation/${type}/${name}/${uid}/edit`} className="Document__a">
                                                <i className="fas fa-edit Document__icon-padding Document__icon-hover" title="Edit" style={{ cursor: "pointer" }}></i>
                                        </Link>
                                ) : (
                                        <i className="fas fa-lock Document__icon-padding" title="Can't Edit" style={{ cursor: "not-allowed" }}></i>
                                )}
                                {uid === userUid ? (
                                        <i className="fas fa-trash Document__icon-padding Document__icon-hover" title="Delete Document" style={{ cursor: "pointer" }} onClick={handleDeleteClick}></i>
                                ) : (
                                        <i className="fas fa-lock Document__icon-padding" title="Can't Delete Document" style={{ cursor: "not-allowed" }}></i>
                                )}
                                {uid === userUid && (
                                        <i className="fas fa-puzzle-piece Document__icon-padding Document__icon-hover" title="Save as template" style={{ cursor: "pointer" }} onClick={() => setShowSaveAsTemplate(true)}></i>
                                )}
                                {uid === userUid && (
                                        <i
                                                className={`fas ${documentResponseData.is_archived ? 'fa-box-open' : 'fa-box-archive'} Document__icon-padding Document__icon-hover`}
                                                title={documentResponseData.is_archived ? "Unarchive" : "Archive"}
                                                style={{ cursor: "pointer" }}
                                                onClick={handleArchiveDocument}
                                        ></i>
                                )}
                                <div className="Document__icon-separator"></div>
                                {documentResponseData.is_public ? (
                                        <i className="fas fa-eye Document__icon-padding" title="Document is Public"></i>
                                ) : (
                                        <i className="fas fa-eye-slash Document__icon-padding" title="Document is not Public"></i>
                                )}
                                {documentResponseData.completed ? (
                                        <i className="fa-solid fa-circle-check Document__icon-padding" title="Published"></i>
                                ) : (
                                        <i className="fa-solid fa-circle-xmark Document__icon-padding" title="Not Published"></i>
                                )}
                                <div className="Document__icon-separator"></div>
                                <i className="fa-solid fa-file-lines Document__icon-padding Document__icon-hover" title="Export as Markdown" style={{ cursor: "pointer" }} onClick={() => handleExportClick('markdown')}></i>
                                <i className="fa-solid fa-file-code Document__icon-padding Document__icon-hover" title="Export as JSON" style={{ cursor: "pointer" }} onClick={() => handleExportClick('json')}></i>
                                <i className="fa-solid fa-file-pdf Document__icon-padding Document__icon-hover" title="Export as PDF" style={{ cursor: "pointer" }} onClick={() => handleExportClick('pdf')}></i>
                                <i className="fa-solid fa-print Document__icon-padding Document__icon-hover" title="Print" style={{ cursor: "pointer" }} onClick={() => window.print()}></i>
                                {youtubeLink && youtubeLink !== "null" || blueskyLink && blueskyLink !== "null" || twitterLink && twitterLink !== "null" || customLink && customLink !== "null" || githubLink && githubLink !== "null" || linkedInLink && linkedInLink !== "null" || discordLink && discordLink !== "null" ? (
                                        <div className="Document__icon-separator"></div>
                                ) : (
                                        <></>
                                )}
                                {customLink && customLink !== "null" ? (
                                        <i className="fa-solid fa-link Document__icon-padding Document__icon-hover" title={customLinkTitle} style={{ cursor: "pointer" }} onClick={navigateToSocialMediaPage(customLink)}></i>
                                ) : (
                                        <></>
                                )}
                                {linkedInLink && linkedInLink !== "null" ? (
                                        <i className="fa-brands fa-linkedin Document__icon-padding Document__icon-hover" title="LinkedIn Profile Link" style={{ cursor: "pointer" }} onClick={navigateToSocialMediaPage(linkedInLink)}></i>
                                ) : (
                                        <></>
                                )}
                                {githubLink && githubLink !== "null" ? (
                                        <i className="fa-brands fa-square-github Document__icon-padding Document__icon-hover" title="GitHub Profile Link" style={{ cursor: "pointer" }} onClick={navigateToSocialMediaPage(githubLink)}></i>
                                ) : (
                                        <></>
                                )}
                                {blueskyLink && blueskyLink !== "null" ? (
                                        <i className="fa-brands fa-bluesky Document__icon-padding Document__icon-hover" title="Bluesky Profile Link" style={{ cursor: "pointer" }} onClick={navigateToSocialMediaPage(blueskyLink)}></i>
                                ) : (
                                        <></>
                                )}
                                {twitterLink && twitterLink !== "null" ? (
                                        <i className="fa-brands fa-square-x-twitter Document__icon-padding Document__icon-hover" title="Twitter Profile Link" style={{ cursor: "pointer" }} onClick={navigateToSocialMediaPage(twitterLink)}></i>
                                ) : (
                                        <></>
                                )}
                                {discordLink && discordLink !== "null" ? (
                                        <i className="fa-brands fa-discord Document__icon-padding Document__icon-hover" title="Discord Server Link" style={{ cursor: "pointer" }} onClick={navigateToSocialMediaPage(discordLink)}></i>
                                ) : (
                                        <></>
                                )}
                                {youtubeLink && youtubeLink !== "null" ? (
                                        <i className="fa-brands fa-youtube Document__icon-padding Document__icon-hover" title="YouTube Channel Link" style={{ cursor: "pointer" }} onClick={navigateToSocialMediaPage(youtubeLink)}></i>
                                ) : (
                                        <></>
                                )}
                        </div>

                        <SaveAsTemplateDialog
                                isOpen={showSaveAsTemplate}
                                onClose={() => setShowSaveAsTemplate(false)}
                                source={{
                                        name,
                                        description: documentResponseData.description || "",
                                        content: rawContent,
                                        docType: type && type.toLowerCase() === "space" ? "Space" : "Page",
                                        hasChildren,
                                        loadDescendants: async () => {
                                                const user = auth.currentUser;
                                                const token = user ? await user.getIdToken() : null;
                                                return fetchPersonalDescendants(documentResponseData.children, token);
                                        },
                                }}
                                onSaved={(saved) => alert(`Template "${saved.name}" saved. Find it under My Templates.`)}
                        />
                        {isShareVisible && (
                                <div className="Document__overlay">
                                        <div className="Document__popup">
                                                <h2>Share {capitalizeFirstLetter(type)}</h2>
                                                {type === "space" && userUid === uid ? (
                                                        <>
                                                                {/* <p>Would you like this space to appear on our public registry? Anyone can search for it from there.</p>
                                                                <label className="Create__switch">
                                                                        <input 
                                                                                type="checkbox"
                                                                                checked={shareOnPublicRegistry}
                                                                                onChange={toggleShareOnPublicRegistry}
                                                                        />
                                                                        <span className="Create__slider round"></span>
                                                                </label> */}
                                                        </>
                                                ) : userUid === uid ? (
                                                        <>
                                                                {/* <p>Would you like this page to appear on our public registry? Anyone can search for it from there.</p>
                                                                <label className="Create__switch">
                                                                        <input 
                                                                                type="checkbox"
                                                                                checked={shareOnPublicRegistry}
                                                                                onChange={toggleShareOnPublicRegistry}
                                                                        />
                                                                        <span className="Create__slider round"></span>
                                                                </label> */}
                                                        </>
                                                ) : (
                                                        <></>
                                                )}
                                                <p>To share a link of this single document, you can copy the link below.</p>
                                                <div className="Document__shareable-link-container">
                                                        <p><span role="textbox" aria-label="Shareable link" className="Document__share-link">{`https://bluebird-documentation.com/documentation/${type}/${encodeURIComponent(name)}/${uid}`}</span></p>
                                                        <i
                                                                className="fa-solid fa-copy Document__copy-icon"
                                                                onClick={copyToClipboard}
                                                                title="Copy to clipboard"
                                                        ></i>
                                                </div>
                                                <button className="Document__modal-close-button" onClick={handleShareClick}>Close</button>
                                                {/* <button className="Document__modal-submit-button" onClick={handleShareOnPublicRegistrySubmit}>SUBMIT</button> */}
                                        </div>
                                </div>
                        )}

                        {exportModal && (
                                <div className="Document__overlay">
                                        <div className="Document__popup Document__popup--export">
                                                <h2>Export as {exportModal.format === 'markdown' ? 'Markdown' : exportModal.format === 'pdf' ? 'PDF' : 'JSON'}</h2>
                                                {exportLoading && exportModal.format !== 'pdf' ? (
                                                        <p style={{ padding: '20px' }}>Fetching child documents...</p>
                                                ) : pdfStatus && pdfStatus.state === 'queued' ? (
                                                        <>
                                                                <p>{pdfStatus.message}</p>
                                                                <button className="Document__modal-submit-button" onClick={() => setExportModal(null)}>Close</button>
                                                        </>
                                                ) : (
                                                        <>
                                                                {hasChildren && (exportModal.format !== 'pdf' || userUid) && (
                                                                        <>
                                                                                <p>What would you like to export?</p>
                                                                                <div className="Document__export-scope">
                                                                                        <label>
                                                                                                <input
                                                                                                        type="radio"
                                                                                                        name="exportScope"
                                                                                                        value="current"
                                                                                                        checked={exportScope === 'current'}
                                                                                                        onChange={() => setExportScope('current')}
                                                                                                />
                                                                                                This document only
                                                                                        </label>
                                                                                        <label>
                                                                                                <input
                                                                                                        type="radio"
                                                                                                        name="exportScope"
                                                                                                        value="all"
                                                                                                        checked={exportScope === 'all'}
                                                                                                        onChange={() => setExportScope('all')}
                                                                                                />
                                                                                                This document + all children <span className="Document__export-hint">{exportModal.format === 'pdf' ? '(one combined PDF)' : '(downloaded as .zip)'}</span>
                                                                                        </label>
                                                                                </div>
                                                                        </>
                                                                )}
                                                                {exportModal.format === 'pdf' && (
                                                                        <PdfExportOptions
                                                                                options={pdfOptions}
                                                                                onChange={setPdfOptions}
                                                                                combined={Boolean(userUid) && hasChildren && exportScope === 'all'}
                                                                                status={pdfStatus}
                                                                        />
                                                                )}
                                                                <button className="Document__modal-close-button" onClick={() => setExportModal(null)} disabled={exportLoading}>Cancel</button>
                                                                <button className="Document__modal-submit-button" onClick={handleExportConfirm} disabled={exportLoading}>
                                                                        {exportLoading ? 'Generating…' : exportModal.format === 'pdf' ? 'Download PDF' : 'Export'}
                                                                </button>
                                                        </>
                                                )}
                                        </div>
                                </div>
                        )}

                        <div className={`DocumentationMainPage__main-content${userUid ? " DocumentationMainPage__main-content--with-comments" : ""}`}>

                                {/* BBD-138: with comments docked below, the document scrolls inside this region */}
                                <div className="Document__scroll">
                                <div className="Document__div">

                                        <h2 className="Document__div-title">{name}</h2>
                                        {/* Shown on paper only (Ctrl+P, BBD-120) */}
                                        {documentResponseData.description && documentResponseData.description !== "null" && (
                                                <p className="Document__print-description">{documentResponseData.description}</p>
                                        )}

                        {documentResponseData.attachments && documentResponseData.attachments.length > 0 && (
                                <div className="Document__attachments-section">
                                        <div className="Document__attachments-list">
                                                {documentResponseData.attachments.map((attachment, index) => (
                                                        <div 
                                                                key={attachment.id || index} 
                                                                className="Document__attachment-item"
                                                        >
                                                                <div className="Document__attachment-info">
                                                                        <div className="Document__attachment-name-row">
                                                                                <i className="fas fa-file-archive Document__attachment-icon"></i>
                                                                                <span className="Document__attachment-name">
                                                                                        {attachment.file_name}
                                                                                </span>
                                                                        </div>
                                                                        <div className="Document__attachment-details">
                                                                                <span className="Document__attachment-detail-item">
                                                                                        <i className="fas fa-weight-hanging"></i>
                                                                                        {formatFileSize(attachment.file_size)}
                                                                                </span>
                                                                                <span className="Document__attachment-detail-item">
                                                                                        <i className="fas fa-download"></i>
                                                                                        {attachment.download_count || 0} downloads
                                                                                </span>
                                                                                <span className="Document__attachment-detail-item">
                                                                                        <i className="fas fa-calendar"></i>
                                                                                        {formatTimestamp(attachment.created_at)}
                                                                                </span>
                                                                        </div>
                                                                        {attachment.description && (
                                                                                <div className="Document__attachment-description">
                                                                                        {attachment.description}
                                                                                </div>
                                                                        )}
                                                                </div>
                                                                <button
                                                                        onClick={() => handleAttachmentDownload(attachment)}
                                                                        className="Document__attachment-download-btn"
                                                                        title={`Download ${attachment.file_name}`}
                                                                >
                                                                        <i className="fas fa-download"></i>
                                                                        Download
                                                                </button>
                                                        </div>
                                                ))}
                                        </div>
                                </div>
                        )}

                        {type && type.toLowerCase() === "space" && documentResponseData.show_table_of_contents !== false && (
                                <SpaceTableOfContents
                                        variant="personal"
                                        spaceName={name}
                                        authorUid={uid}
                                />
                        )}

                                        <p className="extra-small Document__document-views-text"><span className="semi-bold">Views: </span> <i>{documentResponseData.views}</i></p>

                                        <p className="extra-small" style={{ paddingTop: "30px" }}><span className="semi-bold">Created At:</span> <i>{formatTimestamp(documentResponseData.created_at)}</i></p>
                                        <p className="extra-small" style={{ paddingBottom: "30px" }}><span className="semi-bold">Last Updated At:</span> <i>{formatTimestamp(documentResponseData.updated_at)}</i></p>

                                        <div
                                                className="ql-editor ql-editor-styling"
                                                onClick={handleContentClick}
                                                dangerouslySetInnerHTML={{ __html: documentResponseData.content }}
                                        />

                                </div>
                                </div>

                                {userUid && (
                                        <Comments
                                                type={type}
                                                name={name}
                                                uid={uid}
                                                userEmail={userEmail}
                                                userUid={userUid}
                                        />
                                )}

                        </div>

                        {enhancedRightClickEnabled && ContextMenuComponent}

                        {activeTask && (
                                <TaskDetailModal
                                        boardTaskId={activeTask.boardTaskId}
                                        boardId={activeTask.boardId}
                                        taskDbId={activeTask.taskDbId}
                                        onClose={closeTaskModal}
                                />
                        )}

                </div>
        )

}

export default Document